import { ArenaGameState, BidRecord, BidCategory, Direction } from '../../state/ArenaTypes';
import { GameTableConfig } from '../../engine/tableConfig/types';
import { ContractFormatContext } from '../common/IGameRules';
import { IScorePresenter } from '../common/IScorePresenter';
import { IContractFormatterStrategy } from '../common/IContractFormatterStrategy';

/**
 * Authentic Bid Whist Bidding Power Hierarchy:
 * Within the same level N (e.g. at 4):
 * - High / Uptown Trump = Level*10 + 1 (e.g. 41)
 * - Low / Downtown Trump = Level*10 + 2 (e.g. 42 -> Low takes out High!)
 * - No-Trump (Uptown/Downtown NT) = Level*10 + 3 (e.g. 43 -> No takes out both High and Low!)
 * Any (N+1) bid beats all N-level bids (e.g. 5 High = 51 > 43).
 */
export function getBidPower(
  bid: number,
  direction?: Direction | string | null,
  category?: BidCategory | string | null
): number {
  const numBid = Number(bid);
  if (isNaN(numBid) || numBid < 4) return 0;
  const dirUpper = String(direction || 'UP').toUpperCase();
  const catUpper = String(category || 'TRUMP').toUpperCase();
  const subRank = (catUpper === 'NT' || catUpper === 'NOTRUMP' || catUpper === 'NO_TRUMP')
    ? 3
    : (dirUpper === 'DOWN' || dirUpper === 'DOWNTOWN' || dirUpper === 'LOW' || dirUpper === 'SPECIAL')
    ? 2
    : 1;
  return numBid * 10 + subRank;
}

/**
 * Presentation-only contract formatter for Bid Whist auction contracts.
 */
export class BidWhistContractFormatter implements IContractFormatterStrategy {
  public formatContractName(
    rawBid?: number | null,
    direction?: string | null,
    suit?: string | null,
    isNoTrump?: boolean,
    fallbackContract?: string,
    _config?: GameTableConfig
  ): string {
    if (fallbackContract) return fallbackContract;
    if (!rawBid) return 'Contract';
    const dir = direction === 'DOWN' ? 'Downtown' : 'Uptown';
    if (isNoTrump || suit === 'NT' || suit === 'NoTrump') {
      return `${rawBid} ${dir} No-Trump`;
    }
    const suitName = suit === 'H' ? 'Hearts' : suit === 'S' ? 'Spades' : suit === 'C' ? 'Clubs' : suit === 'D' ? 'Diamonds' : (suit || 'Hearts');
    return `${rawBid} ${dir} in ${suitName}`;
  }

  public formatContractOutcome(
    gameState: ArenaGameState,
    context?: ContractFormatContext,
    config?: GameTableConfig,
    _scorePresenter?: IScorePresenter
  ): string {
    const rawBid = gameState.winningBid || 4;
    const suit = gameState.trumpSuit;
    const isNT = Boolean(gameState.isNoTrump || suit === 'NT' || suit === 'NoTrump');
    const fullContract = this.formatContractName(rawBid, gameState.direction, suit, isNT, undefined, config);

    if (!context?.team1Names && !context?.team2Names) {
      return fullContract;
    }

    const bSeat = gameState.winningBidderSeat ?? 0;
    const bTeam = (bSeat === 0 || bSeat === 2) ? 1 : 2;
    const bNames = bTeam === 1 ? (context.team1Names || 'Team 1') : (context.team2Names || 'Team 2');

    // Use authoritative server score history or gameState outcome
    const history = Array.isArray(gameState.scoreHistory) ? gameState.scoreHistory : [];
    const latest = history.find(h => h && h.handNumber === gameState.handNumber) || (history.length > 0 ? history[history.length - 1] : null);
    if (latest) {
      const pts = (bTeam === 1 ? latest.pointsTeam1 : latest.pointsTeam2) ?? 0;
      const ptsSign = pts > 0 ? `+${pts}` : `${pts}`;
      if (latest.result === 'SET' || gameState.isSet) {
        return `${bNames}: ${ptsSign} for failing ${fullContract}`;
      }
      return `${bNames}: ${ptsSign} for making ${fullContract}`;
    }

    if (gameState.isSet !== undefined) {
      if (gameState.isSet) {
        return `${bNames}: failed ${fullContract}`;
      }
      return `${bNames}: made ${fullContract}`;
    }

    return `${bNames}: ${fullContract}`;
  }

  public getContractSummaryText(gameState: ArenaGameState, _config?: GameTableConfig): string | null {
    const raw = (gameState as any).contractSummary;
    if (raw) return String(raw);

    if (gameState.winningBid) {
      const arrow = gameState.direction === 'UP' ? ' ⬆' : (gameState.direction === 'DOWN' ? ' ⬇' : '');
      const suit = gameState.trumpSuit ? ` ${gameState.trumpSuit}` : '';
      return `${gameState.winningBid}${arrow}${suit}`;
    }

    return null;
  }

  public getHighestBidRecord(bids?: BidRecord[]): BidRecord | null {
    if (!bids || bids.length === 0) return null;
    let highest: BidRecord | null = null;
    let highestPower = 0;
    for (const b of bids) {
      if (b && !b.isPass && Number(b.bid ?? 0) >= 4) {
        const power = getBidPower(Number(b.bid), b.direction, b.category);
        if (power > highestPower) {
          highestPower = power;
          highest = b;
        }
      }
    }
    return highest;
  }
}
